import type { ProgramInputSchema } from "@brookbush/types";

import clsx from "clsx";
import type * as React from "react";
import { useEffect, useMemo } from "react";
import { get, useFormContext } from "react-hook-form";
import {
	type ZodArray,
	ZodBoolean,
	type ZodDiscriminatedUnion,
	type ZodEnum,
	ZodNumber,
	type ZodObject,
	ZodString,
	type ZodType,
} from "zod";

import FormCheckbox from "../../components/FormCheckbox";
import FormNumber from "../../components/FormNumber";
import FormSelect from "../../components/FormSelect";
import FormTextArea from "../../components/FormTextArea";
import FormTextField from "../../components/FormTextField";
import ProgramFormUnionInput from "./ProgramFormUnionInput";

interface ProgramFormDynamicInputProps {
	schema: ZodType;
	label: string;
	formKey: string;
	isOptional?: boolean;
}

const ProgramFormDynamicInput: React.FC<ProgramFormDynamicInputProps> = ({
	schema,
	label,
	formKey,
	isOptional,
}) => {
	const {
		formState: { errors },
		watch,
		setValue,
	} = useFormContext<ProgramInputSchema>();

	const isAngle = schema.type === "number" && schema.meta()?.isAngle;
	const isMultiline = schema.type === "string" && schema.meta()?.isMultiline;

	const error = useMemo(
		() => get(errors, formKey) as { message?: string } | undefined,
		[errors, formKey],
	);
	const hasWarning = formKey === "trainingFrequency" && watch(formKey) > 4;

	const disabledWhen = schema.meta()?.disabledWhen as
		| { field: string; value: unknown }
		| undefined;
	const disabledMessage = schema.meta()?.disabledMessage as string | undefined;
	const watchedDisableField = watch(
		(disabledWhen?.field ?? "") as keyof ProgramInputSchema,
	);

	const isDisabled =
		disabledWhen !== undefined && watchedDisableField === disabledWhen.value;

	const isHidden =
		(formKey === "warmupFocus" && watch("trainingFrequency") > 3) ||
		(formKey === "lowerWarmupFocus" && watch("trainingFrequency") <= 3) ||
		(formKey === "upperWarmupFocus" && watch("trainingFrequency") <= 3);

	useEffect(() => {
		if (isDisabled) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			setValue(formKey as keyof ProgramInputSchema, false as any);
		}
	}, [isDisabled, formKey, setValue]);

	if (isHidden) return null;

	return (
		<div
			className={clsx("flex h-fit w-fit items-center gap-3", {
				"flex-col items-start gap-2 laptop:gap-4": schema.type !== "boolean",
			})}
		>
			{schema instanceof ZodBoolean && (
				<FormCheckbox
					formKey={formKey}
					schema={schema}
					isDisabled={isDisabled}
				/>
			)}

			<div className="flex w-full flex-1 flex-col gap-1">
				<div className="flex items-center justify-between gap-4 whitespace-nowrap laptop:text-base text-sm">
					<label htmlFor={formKey} className="font-medium">
						{label}
					</label>

					{error ? (
						<span className="font-normal text-red-500 dark:text-red-300">
							{error?.message}
						</span>
					) : isOptional ? (
						<span className="font-normal text-slate-500 dark:text-slate-300">
							Optional
						</span>
					) : null}
				</div>

				{hasWarning ? (
					<p className="laptop:text-sm text-amber-700 text-xs dark:text-amber-400">
						Warning: High frequency may lead to overtraining.
					</p>
				) : isDisabled && disabledMessage ? (
					<p className="laptop:text-sm text-amber-700 text-xs dark:text-amber-400">
						{disabledMessage}
					</p>
				) : schema.description && schema.type !== "string" ? (
					<p className="laptop:text-sm text-xs">{schema.description}</p>
				) : isAngle ? (
					<p className="laptop:text-sm text-xs">In Degrees</p>
				) : null}
			</div>

			{schema instanceof ZodString ? (
				isMultiline ? (
					<FormTextArea formKey={formKey} schema={schema} />
				) : (
					<FormTextField formKey={formKey} schema={schema} />
				)
			) : schema instanceof ZodNumber ? (
				<FormNumber formKey={formKey} schema={schema} />
			) : schema.type === "enum" || schema.type === "array" ? (
				<FormSelect
					formKey={formKey}
					schema={schema as ZodArray<ZodEnum> | ZodEnum}
				/>
			) : schema.type === "union" ? (
				<ProgramFormUnionInput
					formKey={formKey}
					schema={schema as ZodDiscriminatedUnion<ZodObject[]>}
				/>
			) : null}
		</div>
	);
};

export default ProgramFormDynamicInput;
